const ANALYTICS_ENDPOINT = '/api/analytics/event';
const SESSION_STORAGE_KEY = 'ivg_analytics_session_id';
const SESSION_STARTED_KEY = 'ivg_analytics_session_started_at';
const SESSION_LANDING_KEY = 'ivg_analytics_landing';
const SESSION_RETURNING_KEY = 'ivg_analytics_returning';
const ANALYTICS_OPT_OUT_KEY = 'ivg_analytics_opt_out';
const VISITOR_SEEN_KEY = 'ivg_analytics_visitor_seen';
const PUBLIC_PATHS = new Set(['/', '/playlists', '/support', '/submit-your-music', '/thank-you']);

export function getAnalyticsSessionId() {
  if (typeof window === 'undefined') return '';
  try {
    const existing = window.sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (existing) return existing;
    const generated = `ivg_${Date.now().toString(36)}_${randomIdPart()}`;
    window.sessionStorage.setItem(SESSION_STORAGE_KEY, generated);
    window.sessionStorage.setItem(SESSION_STARTED_KEY, String(Date.now()));
    return generated;
  } catch {
    return `ivg_${Date.now().toString(36)}_${randomIdPart()}`;
  }
}

export function trackPageView(pathname, extra = {}) {
  applyAnalyticsOptOutFromUrl();
  if (isAnalyticsOptedOut()) return;

  const path = normalizePath(pathname || getCurrentPath());
  if (!PUBLIC_PATHS.has(path)) return;
  trackSiteEvent('page_view', {
    page: pageNameFromPath(path),
    path,
    ...extra,
  });
}

export function trackSiteEvent(eventName, params = {}) {
  if (typeof window === 'undefined') return;
  applyAnalyticsOptOutFromUrl();
  if (isAnalyticsOptedOut()) return;

  const path = normalizePath(getCurrentPath());
  const searchParams = new URLSearchParams(window.location.search || '');
  const landing = getSessionLanding(path, searchParams);
  const trackingParams = getTrackingParams(searchParams, landing);
  const payload = {
    session_id: getAnalyticsSessionId(),
    event_name: eventName,
    page: params.page || pageNameFromPath(path),
    path,
    referrer: document.referrer || '',
    ...trackingParams,
    ...getAnalyticsContext(path, searchParams, landing),
    ...cleanParams(params),
  };

  const body = JSON.stringify(payload);
  try {
    if (navigator.sendBeacon) {
      const blob = new Blob([body], { type: 'application/json' });
      if (navigator.sendBeacon(ANALYTICS_ENDPOINT, blob)) return;
    }
  } catch {}

  try {
    fetch(ANALYTICS_ENDPOINT, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body,
      keepalive: true,
    }).catch(() => {});
  } catch {}
}

function applyAnalyticsOptOutFromUrl() {
  if (typeof window === 'undefined') return;
  try {
    const value = new URLSearchParams(window.location.search || '').get('analytics');
    if (value === 'off') {
      window.localStorage.setItem(ANALYTICS_OPT_OUT_KEY, '1');
    } else if (value === 'on') {
      window.localStorage.removeItem(ANALYTICS_OPT_OUT_KEY);
    }
  } catch {}
}

function isAnalyticsOptedOut() {
  if (typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem(ANALYTICS_OPT_OUT_KEY) === '1';
  } catch {
    return false;
  }
}

function getAnalyticsContext(path, searchParams, landing = null) {
  const ua = navigator.userAgent || '';
  const sessionLanding = landing || getSessionLanding(path, searchParams);
  return {
    landing_path: sessionLanding.path,
    landing_source: sessionLanding.source,
    session_age_sec: getSessionAgeSeconds(),
    returning_visitor: getReturningVisitorFlag() ? 1 : 0,
    os: detectOS(ua),
    browser: detectBrowser(ua),
    in_app_browser: detectInAppBrowser(ua),
    viewport: `${window.innerWidth || 0}x${window.innerHeight || 0}`,
    screen: `${window.screen?.width || 0}x${window.screen?.height || 0}`,
    language: navigator.language || '',
    time_zone: getTimeZone(),
  };
}

function getSessionLanding(path, searchParams) {
  try {
    const existing = window.sessionStorage.getItem(SESSION_LANDING_KEY);
    if (existing) return JSON.parse(existing);
  } catch {}

  const landing = {
    path,
    source: landingSource(searchParams),
    ...readUrlTrackingParams(searchParams),
  };
  try {
    window.sessionStorage.setItem(SESSION_LANDING_KEY, JSON.stringify(landing));
  } catch {}
  return landing;
}

function getTrackingParams(searchParams, landing = {}) {
  const current = readUrlTrackingParams(searchParams);
  return {
    utm_source: current.utm_source || landing.utm_source || '',
    utm_medium: current.utm_medium || landing.utm_medium || '',
    utm_campaign: current.utm_campaign || landing.utm_campaign || '',
    utm_content: current.utm_content || landing.utm_content || '',
    utm_term: current.utm_term || landing.utm_term || '',
    meta_source: current.meta_source || landing.meta_source || '',
    meta_placement: current.meta_placement || landing.meta_placement || '',
    meta_ad_id: current.meta_ad_id || landing.meta_ad_id || '',
    meta_adset_id: current.meta_adset_id || landing.meta_adset_id || '',
    meta_campaign_id: current.meta_campaign_id || landing.meta_campaign_id || '',
    fbclid_present: current.fbclid_present || landing.fbclid_present ? 1 : 0,
  };
}

function readUrlTrackingParams(searchParams) {
  return {
    utm_source: searchParams.get('utm_source') || '',
    utm_medium: searchParams.get('utm_medium') || '',
    utm_campaign: searchParams.get('utm_campaign') || '',
    utm_content: searchParams.get('utm_content') || '',
    utm_term: searchParams.get('utm_term') || '',
    meta_source: searchParams.get('meta_source') || searchParams.get('site_source_name') || '',
    meta_placement: searchParams.get('meta_placement') || '',
    meta_ad_id: searchParams.get('meta_ad_id') || searchParams.get('ad_id') || '',
    meta_adset_id: searchParams.get('meta_adset_id') || searchParams.get('adset_id') || '',
    meta_campaign_id: searchParams.get('meta_campaign_id') || searchParams.get('campaign_id') || '',
    fbclid_present: searchParams.has('fbclid') ? 1 : 0,
  };
}

function landingSource(searchParams) {
  const source = searchParams.get('utm_source') || '';
  const medium = searchParams.get('utm_medium') || '';
  if (source) return medium ? `${source} / ${medium}` : source;
  const referrer = document.referrer || '';
  if (!referrer) return 'Direct / none';
  try {
    const host = new URL(referrer).hostname.replace(/^www\./, '');
    return host === window.location.hostname.replace(/^www\./, '') ? 'Internal / same-site' : host;
  } catch {
    return referrer.slice(0, 80);
  }
}

function getSessionAgeSeconds() {
  try {
    const started = Number(window.sessionStorage.getItem(SESSION_STARTED_KEY)) || Date.now();
    return Math.max(0, Math.round((Date.now() - started) / 1000));
  } catch {
    return 0;
  }
}

function getReturningVisitorFlag() {
  try {
    const existing = window.sessionStorage.getItem(SESSION_RETURNING_KEY);
    if (existing === '1') return true;
    if (existing === '0') return false;
    const seen = window.localStorage.getItem(VISITOR_SEEN_KEY) === '1';
    window.sessionStorage.setItem(SESSION_RETURNING_KEY, seen ? '1' : '0');
    window.localStorage.setItem(VISITOR_SEEN_KEY, '1');
    return seen;
  } catch {
    return false;
  }
}

function detectOS(ua) {
  const text = String(ua || '');
  const platform = String(navigator.platform || '');
  if (/iphone|ipad|ipod/i.test(text) || (/Mac/i.test(platform) && navigator.maxTouchPoints > 1)) return 'iOS';
  if (/android/i.test(text)) return 'Android';
  if (/windows/i.test(text)) return 'Windows';
  if (/mac os|macintosh|macintel|macppc|mac68k/i.test(text) || /Mac/i.test(platform)) return 'macOS';
  if (/linux/i.test(text)) return 'Linux';
  return 'Unknown';
}

function detectBrowser(ua) {
  const text = String(ua || '');
  if (/Instagram/i.test(text)) return 'Instagram in-app';
  if (/FBAN|FBAV|FBIOS|FB_IAB|FB4A/i.test(text)) return 'Facebook in-app';
  if (/Edg\//i.test(text)) return 'Edge';
  if (/CriOS|Chrome\//i.test(text) && !/Chromium/i.test(text)) return 'Chrome';
  if (/FxiOS|Firefox\//i.test(text)) return 'Firefox';
  if (/Version\/[\d.]+.*Safari/i.test(text)) return 'Safari';
  return 'Unknown';
}

function detectInAppBrowser(ua) {
  const text = String(ua || '');
  if (/Instagram/i.test(text)) return 'Instagram';
  if (/FBAN|FBAV|FBIOS|FB_IAB|FB4A/i.test(text)) return 'Facebook';
  if (/TikTok/i.test(text)) return 'TikTok';
  return '';
}

function getTimeZone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || '';
  } catch {
    return '';
  }
}

function cleanParams(params = {}) {
  const out = {};
  Object.entries(params || {}).forEach(([key, value]) => {
    if (value == null) return;
    if (typeof value === 'boolean' || typeof value === 'number') {
      out[key] = value;
      return;
    }
    const text = String(value).trim();
    if (text) out[key] = text;
  });
  return out;
}

function pageNameFromPath(pathname) {
  const path = normalizePath(pathname);
  if (path === '/') return 'home';
  return path.replace(/^\/+/, '').replace(/-/g, '_') || 'home';
}

function normalizePath(pathname) {
  const normalized = String(pathname || '/').replace(/\/+$/, '');
  return normalized || '/';
}

function getCurrentPath() {
  return window.location?.pathname || '/';
}

function randomIdPart() {
  try {
    const array = new Uint32Array(2);
    window.crypto.getRandomValues(array);
    return Array.from(array, (value) => value.toString(36)).join('');
  } catch {
    return Math.random().toString(36).slice(2, 12);
  }
}
